/**
 * Error Boundary Component
 * 
 * Catches React errors and displays a fallback UI with recovery options.
 * Integrates with error monitoring service (Sentry) for production error tracking.
 * 
 * Requirements: 14.1, 14.2, 14.3, 14.8
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { captureException, setSentryContext } from '../lib/sentry';
import { useTranslation } from 'react-i18next';

/**
 * Default fallback UI as a functional component so it can use the useTranslation hook.
 */
function DefaultErrorFallback({ error, onReload, onGoHome }: {
  error: Error;
  onReload: () => void;
  onGoHome: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <CardTitle>{t('errorBoundary:somethingWentWrong')}</CardTitle>
          </div>
          <CardDescription>
            An unexpected error occurred. You can try reloading the page or return to the home page.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="rounded-md bg-muted p-4">
            <p className="text-sm font-medium text-muted-foreground mb-2">{t('errorBoundary:errorDetails')}</p>
            <p className="text-sm font-mono text-destructive break-all">
              {error.message}
            </p>

            {process.env.NODE_ENV === 'development' && error.stack && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  Stack Trace
                </summary>
                <pre className="mt-2 text-xs overflow-auto max-h-48 text-muted-foreground">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex gap-2">
          <Button
            onClick={onReload}
            variant="default"
            className="flex-1"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload Page
          </Button>
          <Button
            onClick={onGoHome}
            variant="outline"
            className="flex-1"
          >
            <Home className="mr-2 h-4 w-4" />
            Go Home
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'root' | 'route' | 'component';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console (Requirement 14.3)
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error info in state
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Send to error monitoring service (Requirement 14.8)
    this.logErrorToService(error, errorInfo);
  }

  /**
   * Log error to monitoring service (Sentry, etc.)
   * Requirement 14.8
   */
  private logErrorToService(error: Error, errorInfo: ErrorInfo): void {
    // Send to Sentry with context
    try {
      // Set error context
      setSentryContext('errorBoundary', {
        level: this.props.level || 'component',
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      });
      
      setSentryContext('browser', {
        userAgent: navigator.userAgent,
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      });
      
      // Capture exception with Sentry
      captureException(error, {
        react: {
          componentStack: errorInfo.componentStack,
        },
      });
    } catch {
      // Sentry may not be initialized — don't let logging crash the error boundary
    }
    
    // Also log to console for development
    console.error('[Error Monitoring]', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      level: this.props.level || 'component',
    });
  }

  /**
   * Reset error boundary state
   */
  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Navigate to home page
   */
  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  /**
   * Reload the current page
   */
  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo!,
          this.handleReset
        );
      }

      // Default fallback UI (Requirement 14.2)
      return (
        <DefaultErrorFallback
          error={this.state.error}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error boundary wrapper for functional components
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
